import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { CustomSteleType } from 'src/components/3d/Stele';
import { useUser } from 'src/context/UserProvider';
import paths from 'src/navigation/paths';
import { ModalType } from 'src/Screens/Configurator';
import { QuoteStep } from 'src/Screens/project/components/NavBar';
import { Assembly } from 'src/services/assemblies';
import { emptyAddress, optionalSchemaAddress } from 'src/utils/address.utils';
import * as THREE from 'three';
import { z } from 'zod';
import { PieceDetail } from './useConfigurator';
import { useConfiguratorSave } from './useConfiguratorSave';
import { ConfiguratorModals } from './ConfiguratorModals';

// --- Schema & constantes ---

export type ClientInformationsFormSchema = z.infer<
  typeof clientInformationsFormSchema
>;

export const clientInformationsFormSchema = z.object({
  madeByFirstName: z.string(),
  madeByLastName: z.string(),
  clientFirstName: z.string(),
  clientLastName: z.string(),
  clientAddress: optionalSchemaAddress,
  cemetary: z.string(),
  concessionName: z.string(),
  concessionHeight: z.number(),
  concessionWidth: z.number(),
  useDimensions: z.boolean(),
  familyRef: z.string(),
});

const stepToNextStep: { [step: string]: QuoteStep } = {
  CUSTOMIZATION: 'ACCESSORIES',
  ACCESSORIES: 'PATTERNS',
  PATTERNS: 'QUOTE',
};

const LAST_STEP: QuoteStep = 'QUOTE';

// --- Hook principal ---

export const useConfiguratorNavigation = (
  pieces: any[],
  customSteles: CustomSteleType[],
  activePieces: any[],
  customPlatings: any[],
  customSoles: any[],
  customBoxes: any[],
  patterns: {
    [key: string]: {
      [key: string]: any;
    }[];
  },
  assemblyId: string | undefined,
  gl: THREE.WebGLRenderer | undefined,
  assembly: Assembly | null,
  onRefresh: () => void,
  setOpenedModal: (openedModal: ModalType | undefined) => void,
  getAssemblyDetails: () => Promise<{
    underAssemblies: {
      pieces: PieceDetail[];
      underAssemblyName: string;
      underAssemblyUUID: string;
      x: number;
      y: number;
      z: number;
      size: number;
    }[];
    pieces: PieceDetail[];
    steles: PieceDetail[];
    platings: PieceDetail[];
    soles: PieceDetail[];
    boxes: PieceDetail[];
    accesories: PieceDetail[];
    patterns: {
      ttcPrice: number;
      htPrice: number;
      name: string;
      description: string;
      quantity: number;
      extra: any[];
      leaderGranitEngraving: boolean;
    }[];
    engravings: any[];
  }>,
  orbitRef: any,
  takePictures: () => Promise<string[]>,
) => {
  // --- Formulaire ---
  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    getValues,
  } = useForm<ClientInformationsFormSchema>({
    defaultValues: {
      clientFirstName: assembly?.quote?.clientFirstName ?? '',
      clientLastName: assembly?.quote?.clientLastName ?? '',
      madeByFirstName: assembly?.quote?.madeByFirstName ?? '',
      madeByLastName: assembly?.quote?.madeByLastName ?? '',
      clientAddress: assembly?.quote?.clientAddress ?? emptyAddress,
      cemetary: assembly?.quote?.cemetary ?? '',
      concessionName: assembly?.quote?.concessionName ?? '',
      concessionHeight: assembly?.quote?.concessionHeight ?? 0,
      concessionWidth: assembly?.quote?.concessionWidth ?? 0,
      useDimensions: assembly?.quote?.useDimensions ?? false,
      familyRef: assembly?.quote?.familyRef ?? '',
    },
    mode: 'onSubmit',
    resolver: zodResolver(clientInformationsFormSchema),
  });

  const { user } = useUser();

  // --- État de navigation ---
  const [step, setStep] = useState<QuoteStep>('CUSTOMIZATION');
  const [selectedPage, setSelectedPage] = useState<string>('pieces');
  const [activeAdditionalServices, setActiveAdditionalServices] = useState<
    string[]
  >([]);
  const [activeInstallations, setActiveInstallations] = useState<
    {
      price: number;
      name: string;
      display: boolean;
    }[]
  >([]);

  const navigate = useNavigate();
  const { pathname } = useLocation();

  // --- Sauvegarde / soumission ---
  const { isLoading, handleSave, submit } = useConfiguratorSave(
    pieces,
    customSteles,
    activePieces,
    customPlatings,
    customSoles,
    customBoxes,
    patterns,
    assemblyId,
    gl,
    assembly,
    onRefresh,
    getAssemblyDetails,
    takePictures,
    step,
    getValues,
  );

  // --- Navigation entre étapes ---
  const stepToPrevStep: { [step: string]: QuoteStep } = Object.entries(
    stepToNextStep,
  ).reduce((acc, [key, value]) => {
    acc[value] = key as QuoteStep;
    return acc;
  }, {} as { [step: string]: QuoteStep });

  const handleNextStep = useCallback(async () => {
    if (
      step === 'ACCESSORIES' &&
      customPlatings.length === 0 &&
      customSteles.length === 0 &&
      activePieces.length === 0 &&
      customSoles.length === 0 &&
      customBoxes.length === 0
    ) {
      return;
    }
    if (step === 'CUSTOMIZATION' || step === 'ACCESSORIES') {
      let allPiecesHaveTexture = true;

      for (const el of activePieces) {
        if (el.texture === null) allPiecesHaveTexture = false;
      }
      for (const el of customSteles) {
        if (el.texture === null) allPiecesHaveTexture = false;
      }
      for (const el of customPlatings) {
        if (el.texture === null) allPiecesHaveTexture = false;
      }
      for (const el of customSoles) {
        if (el.texture === null) allPiecesHaveTexture = false;
      }
      for (const el of customBoxes) {
        if (el.texture === null) allPiecesHaveTexture = false;
      }

      if (!allPiecesHaveTexture) {
        setSelectedPage('textures');
        // return;
      }
    }

    await handleSave();
    const nextStep = stepToNextStep[step] ?? LAST_STEP;
    setStep(nextStep);
  }, [
    step,
    customPlatings,
    customSteles,
    activePieces,
    customSoles,
    customBoxes,
    assembly?.quote?.status,
    handleSave,
  ]);

  const handlePrevStep = useCallback(() => {
    const prevStep = stepToPrevStep[step];
    if (prevStep) {
      setStep(prevStep);
    } else {
      navigate(
        pathname.replace(
          paths.home.project.configurator.root,
          paths.home.project.catalog.root,
        ),
      );
    }
  }, [navigate, pathname, step, stepToPrevStep]);

  // --- Services additionnels ---
  useEffect(() => {
    if (assembly?.quote) {
      setActiveAdditionalServices(
        assembly.quote.additionalServicesOnQuote
          ? assembly.quote.additionalServicesOnQuote.map(
              (el) => el.additionalService.id,
            )
          : [],
      );
    }
  }, [assembly?.quote]);

  // --- Composant modal ---
  const modalComponent = (modal: ModalType): JSX.Element => {
    return (
      <ConfiguratorModals
        modal={modal}
        setOpenedModal={setOpenedModal}
        assembly={assembly}
        onRefresh={onRefresh}
        user={user}
        control={control}
        errors={errors}
        handleSubmit={handleSubmit}
        reset={reset}
        activeAdditionalServices={activeAdditionalServices}
        setActiveAdditionalServices={setActiveAdditionalServices}
        activeInstallations={activeInstallations}
        setActiveInstallations={setActiveInstallations}
      />
    );
  };

  return {
    handleNextStep,
    handlePrevStep,
    submit,
    modalComponent,
    step,
    selectedPage,
    setSelectedPage,
    setStep,
    isLoading,
    activeInstallations,
    setActiveInstallations,
    handleSave,
  };
};
